import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import React, { memo, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import PoweredByStripeLogo from '../../../assets/powered-by-stripe-white.svg';
import { toggleContactModalState } from '../../../ducks/contact.duck';
import {
  androidAppLink,
  appleAppLink,
  getFAQLinks,
  getHelpCenterLink,
  getMapLink,
  getSignUpLink,
  googleReviewLink,
  shopLink,
  stripeLink,
  trustpilotLink,
} from '../../../marketplace-links';
import routeConfiguration from '../../../routeConfiguration';
import { neutral, petrol } from '../../../styles/mui/colors';
import { openCookieSettings } from '../../../util/consent/consentUI';
import { pathByRouteName } from '../../../util/routes';
import Link from '../../Links/Link/Link';
import LogoApplePay from '../../Logo/LogoApplePay';
import LogoGooglePay from '../../Logo/LogoGooglePay';
import LogoKlarna from '../../Logo/LogoKlarna';
import LogoMastercard from '../../Logo/LogoMastercard';
import LogoPayPal from '../../Logo/LogoPayPal';
import LogoSepa from '../../Logo/LogoSepa';
import LogoVisa from '../../Logo/LogoVisa';
import { renderSocialMediaLinks } from './SocialMediaLinks';
import VansiteWhiteLogo from './VansiteWhiteLogo';

// ================ Alts ================ //
const stripeAlt = 'Powered by Stripe';

// =============== Styles =============== //
const containerStyle = {
  backgroundColor: petrol.darkest,
  pt: 4,
  pb: { xs: 12, md: 4 },
};
const categoryHeaderStyle = {
  color: 'white',
  fontWeight: 'bold',
  mb: 1,
  mt: { xs: 4, md: 0 },
};
const subLinkStyle = {
  color: neutral[300],
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
  mb: 0.5,
};
const smallTermsLinkStyle = {
  color: neutral[300],
  fontSize: '0.75rem',
  marginRight: '0.5rem',
  textDecoration: 'none',
};
// Same look as smallTermsLinkStyle, but for entries rendered as a native
// <button> (keyboard-focusable, Enter/Space activate) instead of a link.
const smallTermsButtonStyle = {
  ...smallTermsLinkStyle,
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  p: 0,
};
const dividerStyle = { my: 4, borderColor: neutral[600], width: 1 };
const logoStyle = { fontSize: '48px' };

const generateFooterLinks = (intl, locale, { handleSupportContactForm }) => [
  {
    text: intl.formatMessage({ id: 'AccountBottomBarBox.appHeader' }),
    links: [
      {
        subLinks: [
          {
            text: intl.formatMessage({ id: 'AccountBottomBarBox.appiOS' }),
            href: appleAppLink,
          },
          {
            text: intl.formatMessage({ id: 'AccountBottomBarBox.appAndroid' }),
            href: androidAppLink,
          },
        ],
      },
    ],
  },
  {
    text: intl.formatMessage({ id: 'Footer.guests' }),
    links: [
      {
        subLinks: [
          {
            text: intl.formatMessage({ id: 'Footer.guestsFAQ' }),
            href: getFAQLinks(locale, 'customer'),
          },
          {
            text: intl.formatMessage({ id: 'Footer.guestsRules' }),
            href: pathByRouteName(
              'StoryblokPage',
              routeConfiguration(),
              { slug: 'verhaltensregeln' },
              intl.locale === 'de' ? 'de' : 'en',
            ),
          },
          {
            text: intl.formatMessage({ id: 'Footer.map' }),
            href: getMapLink(locale),
          },
          {
            text: intl.formatMessage({ id: 'Footer.register' }),
            href: getSignUpLink(locale),
          },
        ],
      },
    ],
  },
  {
    text: intl.formatMessage({ id: 'Footer.hosts' }),
    links: [
      {
        subLinks: [
          {
            text: intl.formatMessage({ id: 'Footer.hostsFAQ' }),
            href: getFAQLinks(locale, 'host'),
          },
          {
            text: intl.formatMessage({ id: 'Footer.hostsBecomeHost' }),
            href: pathByRouteName(
              'StoryblokPage',
              routeConfiguration(),
              { slug: 'gastgeber-werden' },
              intl.locale === 'de' ? 'de' : 'en',
            ),
          },
        ],
      },
    ],
  },
  {
    text: intl.formatMessage({ id: 'Footer.help' }),
    links: [
      {
        subLinks: [
          {
            text: intl.formatMessage({ id: 'Footer.helpCenter' }),
            href: getHelpCenterLink(locale),
          },
          {
            text: intl.formatMessage({ id: 'Footer.supportChat' }),
            onClick: handleSupportContactForm,
          },
          {
            text: intl.formatMessage({ id: 'Footer.experience' }),
            href: pathByRouteName(
              'StoryblokPage',
              routeConfiguration(),
              { slug: 'erfahrungen' },
              intl.locale === 'de' ? 'de' : 'en',
            ),
          },
        ],
      },
    ],
  },
  {
    text: intl.formatMessage({ id: 'Footer.vansite' }),
    links: [
      {
        subLinks: [
          {
            text: intl.formatMessage({ id: 'Footer.aboutUs' }),
            href: pathByRouteName(
              'StoryblokPage',
              routeConfiguration(),
              { slug: 'ueber-uns' },
              intl.locale === 'de' ? 'de' : 'en',
            ),
          },
          {
            text: intl.formatMessage({ id: 'Footer.writeGoogleReview' }),
            href: googleReviewLink,
          },
          {
            text: intl.formatMessage({ id: 'Footer.writeTrustpilotReview' }),
            href: trustpilotLink,
          },
          {
            text: intl.formatMessage({ id: 'Footer.career' }),
            href: pathByRouteName(
              'StoryblokPage',
              routeConfiguration(),
              { slug: 'karriere' },
              intl.locale === 'de' ? 'de' : 'en',
            ),
          },
          {
            text: intl.formatMessage({ id: 'Footer.partner' }),
            href: pathByRouteName(
              'StoryblokPage',
              routeConfiguration(),
              { slug: 'partner' },
              intl.locale === 'de' ? 'de' : 'en',
            ),
          },
        ],
      },
    ],
  },
  {
    text: intl.formatMessage({ id: 'Footer.else' }),
    links: [
      {
        subLinks: [
          {
            text: intl.formatMessage({ id: 'Footer.shop' }),
            href: shopLink,
          },
          {
            text: intl.formatMessage({ id: 'Footer.blog' }),
            href: pathByRouteName(
              'StoryblokPage',
              routeConfiguration(),
              { slug: 'blog' },
              intl.locale === 'de' ? 'de' : 'en',
            ),
          },
          {
            text: intl.formatMessage({ id: 'Footer.benefitsWorld' }),
            href: pathByRouteName(
              'StoryblokPage',
              routeConfiguration(),
              { slug: 'pass', rest: 'vorteilswelt' },
              intl.locale === 'de' ? 'de' : 'en',
            ),
          },
          {
            text: intl.formatMessage({ id: 'Footer.newsletter' }),
            href: pathByRouteName(
              'StoryblokPage',
              routeConfiguration(),
              { slug: 'newsletter' },
              intl.locale === 'de' ? 'de' : 'en',
            ),
          },
        ],
      },
    ],
  },
];

const generateTermsLinks = (intl) => [
  {
    text: intl.formatMessage({ id: 'Footer.copyright' }),
    href: pathByRouteName('LandingPage', routeConfiguration(), {}, intl.locale),
  },
  {
    text: intl.formatMessage({ id: 'Footer.privacy' }),
    href: pathByRouteName(
      'StoryblokPage',
      routeConfiguration(),
      { slug: 'privacy-policy' },
      intl.locale === 'de' ? 'de' : 'en',
    ),
  },
  {
    text: intl.formatMessage({ id: 'Footer.terms' }),
    href: pathByRouteName(
      'StoryblokPage',
      routeConfiguration(),
      { slug: 'terms-of-service' },
      intl.locale === 'de' ? 'de' : 'en',
    ),
  },
  {
    text: intl.formatMessage({ id: 'Footer.impressum' }),
    href: pathByRouteName(
      'StoryblokPage',
      routeConfiguration(),
      { slug: 'impressum' },
      intl.locale === 'de' ? 'de' : 'en',
    ),
  },
  {
    text: intl.formatMessage({ id: 'Footer.press' }),
    href: pathByRouteName(
      'StoryblokPage',
      routeConfiguration(),
      { slug: 'news-und-presse' },
      intl.locale === 'de' ? 'de' : 'en',
    ),
  },
  ...(intl.locale === 'de'
    ? [
      {
        text: intl.formatMessage({ id: 'Footer.lotteryText' }),
        href: pathByRouteName(
          'StoryblokPage',
          routeConfiguration(),
          { slug: 'gewinnspiele' },
          'de',
        ),
      },
    ]
    : []),
  {
    text: intl.formatMessage({ id: 'Footer.cookieSettings' }),
    onClick: openCookieSettings,
  },
];

const FooterComponent = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { locale } = intl;

  const handleSupportContactForm = () => {
    dispatch(toggleContactModalState(true));
  };

  // Use useMemo to only recompute footerLinks and termsLinks when dependencies change
  const footerLinks = useMemo(
    () => generateFooterLinks(intl, locale, { handleSupportContactForm }),
    [intl, locale],
  );

  const termsLinks = useMemo(() => generateTermsLinks(intl), [intl]);

  const renderSubLink = (subLink, index) => {
    if (subLink.onClick) {
      return (
        <Typography
          key={index}
          onClick={subLink.onClick}
          sx={subLinkStyle}
          variant="body2"
        >
          {subLink.text}
        </Typography>
      );
    }

    return (
      <Link
        key={subLink.text}
        sx={subLinkStyle}
        target="_blank"
        to={subLink.href || '/'}
        underline="none"
      >
        {subLink.text}
      </Link>
    );
  };

  const renderLinks = () => {
    return footerLinks.map((category) => (
      <Grid container direction="column" key={category.text} size={{ md: 2, sm: 6, xs: 12 }}>
        <Typography sx={categoryHeaderStyle} variant="body1">
          {category.text}
        </Typography>
        {category.links && category.links.length > 0
          ? category.links.map((link, linkIndex) => (
            <React.Fragment key={`link-${linkIndex}`}>
              {link.subLinks && link.subLinks.length > 0
                ? link.subLinks.map(renderSubLink)
                : null}
            </React.Fragment>
          ))
          : null}
      </Grid>
    ));
  };

  const renderTermsLinks = () => {
    return termsLinks.map((link) =>
      link.onClick ? (
        <Typography
          component="button"
          key={link.text}
          onClick={link.onClick}
          sx={smallTermsButtonStyle}
          type="button"
          variant="body2"
        >
          {link.text}
        </Typography>
      ) : (
        <Link key={link.href} sx={smallTermsLinkStyle} to={link.href}>
          {link.text}
        </Link>
      ),
    );
  };

  return (
    <Box sx={containerStyle}>
      <Grid container justifyContent="center">
        {/* Link Groups*/}
        <Grid
          alignItems="center"
          container
          justifyContent={{ xs: 'center', md: 'space-between' }}
          size={10}
          spacing={2}
        >
          <Grid container justifyContent="flex-end" size={12}>
            {renderLinks()}
          </Grid>
        </Grid>

        <Divider sx={dividerStyle} />

        {/* Social Media Section */}
        <Grid container justifyContent="center" size={{ md: 4, xs: 12 }}>
          <Box alignItems="center" display="flex" flexDirection="column" gap={1} width="100%">
            <Typography alignSelf="center" sx={categoryHeaderStyle} variant="body1">
              {intl.formatMessage({ id: 'Footer.followUs' })}
            </Typography>
            <Box alignItems="center" display="flex" gap={4} justifyContent="center">
              {renderSocialMediaLinks(intl)}
            </Box>
          </Box>
        </Grid>

        {/* Payment Methods Section */}
        <Grid container justifyContent="center" size={10}>
          <Box alignItems="center" display="flex" flexDirection="column" gap={1} width="100%">
            <Typography alignSelf="center" sx={categoryHeaderStyle} variant="body1">
              <FormattedMessage id="Footer.paymentHeading" />
            </Typography>
            <Box
              alignItems="center"
              bgcolor="white"
              borderRadius={2}
              columnGap={2}
              display="flex"
              flexWrap="wrap"
              justifyContent="center"
              px={2}
              rowGap={0}
            >
              <LogoPayPal sx={logoStyle} />
              <LogoMastercard sx={logoStyle} />
              <LogoVisa sx={logoStyle} />
              <LogoSepa sx={logoStyle} />
              <LogoKlarna sx={logoStyle} />
              <LogoApplePay sx={logoStyle} />
              <LogoGooglePay sx={logoStyle} />
            </Box>
            <Box height="auto" width="120px">
              <a href={stripeLink} rel="noreferrer nofollow" target="_blank">
                <img alt={stripeAlt} src={PoweredByStripeLogo} />
              </a>
            </Box>
          </Box>
        </Grid>

        <Divider sx={dividerStyle} />

        {/* Bottom Section, Logo, Terms */}
        <Grid
          alignItems="center"
          container
          justifyContent={{ xs: 'center', md: 'space-between' }}
          size={10}
          spacing={2}
        >
          <Grid container justifyContent="center" size={12}>
            <VansiteWhiteLogo />
          </Grid>
          <Grid container justifyContent="center" size={12}>
            {renderTermsLinks()}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

FooterComponent.displayName = 'Footer';
const Footer = memo(FooterComponent);

export default Footer;
